import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Typography } from '@material-ui/core';
import FormSpinner from 'components/FormSpinner';
import { notify } from 'reducers/NotifierReducer';
import { RMSButton } from '../../shared/components/buttons';
import { formatDateTime } from '../../shared/utility/dateFormatter';
import useUserSignature from '../hooks/useUserSignature';
import SetupSignatureDialog from '../components/signature/SetupSignatureDialog';
import ChangeSignatureDrawDialog from '../components/signature/ChangeSignatureDrawDialog';
import ChangePinDialog from '../components/signature/ChangePinDialog';
import PinEntryDialog from '../components/signature/PinEntryDialog';
import { verifyPin, deleteSignature, resetSignaturePin } from '../services/userSignatureService';
import useConfirmDialog from 'features/shared/utility/useConfirmDialog';
import { executeApiAction } from 'features/shared/utility/apiHelper';
import { RMSConfirmationDialog } from 'features/shared/components/dialog/index';

const ManageSignaturePage = () => {
  const dispatch = useDispatch();
  const { signature, signatureLoading, refetchSignature } = useUserSignature();

  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [changePinDialogOpen, setChangePinDialogOpen] = useState(false);
  const [changeSignatureVerifyOpen, setChangeSignatureVerifyOpen] = useState(false);
  const [changeSignatureDrawOpen, setChangeSignatureDrawOpen] = useState(false);
  const [deleteVerifyOpen, setDeleteVerifyOpen] = useState(false);
  const [verifiedPin, setVerifiedPin] = useState('');

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const closeChangeSignatureDraw = useCallback(() => {
    setChangeSignatureDrawOpen(false);
    setVerifiedPin('');
  }, []);

  const handleChangeSignatureVerified = useCallback((pin) => {
    setVerifiedPin(pin);
    setChangeSignatureVerifyOpen(false);
    setChangeSignatureDrawOpen(true);
  }, []);

  const handleDeleteConfirmed = useCallback(() => {
    dispatch(notify('Signature deleted successfully', 'success'));
    setDeleteVerifyOpen(false);
    refetchSignature();
  }, [dispatch, refetchSignature]);

  const handleResetPin = useCallback(() => {
    requestConfirm(
      'Are you sure you want to reset the PIN?',
      async () => {
        await executeApiAction({
          setLoading,
          dispatch,
          action: async () => {
            return resetSignaturePin();
          },
          onSuccess: async () => {
            dispatch(
              notify(
                `Signature PIN has been reset successfully. Please check your email for the new PIN.`,
                'success'
              )
            );
          },
        });
      },
      'Reset Signature PIN'
    );
  }, [dispatch, requestConfirm]);

  return (
    <div className="manage-signature">
      {(signatureLoading || loading) && <FormSpinner />}

      {signature?.hasSignature ? (
        <div className="manage-signature__current">
          <img
            src={signature.signatureData}
            alt="Your signature"
            className="manage-signature__preview"
          />

          <Typography className="manage-signature__meta">
            Last updated: {formatDateTime(signature.Updated || signature.Created)}
          </Typography>

          <div className="manage-signature__actions">
            <RMSButton
              label="Change Signature"
              variant="outlined"
              onClick={() => setChangeSignatureVerifyOpen(true)}
            />
            <RMSButton
              label="Change PIN"
              variant="outlined"
              onClick={() => setChangePinDialogOpen(true)}
            />
            <RMSButton label="Reset PIN" variant="outlined" onClick={() => handleResetPin()} />
            <RMSButton
              label="Delete Signature"
              variant="outlined"
              color="secondary"
              onClick={() => setDeleteVerifyOpen(true)}
            />
          </div>
        </div>
      ) : (
        !signatureLoading && (
          <div className="manage-signature__empty">
            <Typography>You have not set up a signature yet.</Typography>
            <RMSButton
              label="Set Up Signature"
              color="primary"
              onClick={() => setSetupDialogOpen(true)}
            />
          </div>
        )
      )}

      {setupDialogOpen && (
        <SetupSignatureDialog
          open={setupDialogOpen}
          onClose={() => setSetupDialogOpen(false)}
          onSaved={refetchSignature}
        />
      )}

      {changePinDialogOpen && (
        <ChangePinDialog open={changePinDialogOpen} onClose={() => setChangePinDialogOpen(false)} />
      )}

      {changeSignatureVerifyOpen && (
        <PinEntryDialog
          open={changeSignatureVerifyOpen}
          title="Verify PIN"
          description="Enter your current PIN to change your signature."
          submitLabel="Verify"
          onClose={() => setChangeSignatureVerifyOpen(false)}
          onSubmit={verifyPin}
          onVerified={handleChangeSignatureVerified}
        />
      )}

      {changeSignatureDrawOpen && (
        <ChangeSignatureDrawDialog
          open={changeSignatureDrawOpen}
          pin={verifiedPin}
          onClose={closeChangeSignatureDraw}
          onSaved={refetchSignature}
        />
      )}

      {deleteVerifyOpen && (
        <PinEntryDialog
          open={deleteVerifyOpen}
          title="Delete Signature"
          description="Enter your PIN to confirm deleting your signature."
          submitLabel="Delete"
          onClose={() => setDeleteVerifyOpen(false)}
          onSubmit={deleteSignature}
          onVerified={handleDeleteConfirmed}
        />
      )}

      <RMSConfirmationDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onNo={handleNo}
        onYes={handleYes}
        loading={loading}
      />
    </div>
  );
};

export default ManageSignaturePage;
